import {
  arrow,
  autoUpdate,
  FloatingArrow,
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  flip,
  type OffsetOptions,
  type OpenChangeReason,
  offset,
  type Placement,
  type ReferenceType,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
} from '@floating-ui/react'
import { css, cx } from '@linaria/core'
import { type DOMAttributes, type FC, type ReactNode, type Ref, useEffect, useRef } from 'react'
import { style } from '#styles'

const overlayClass = css`
  // background: color-mix(in srgb, ${style.themeColors.backgroundPrimary} 33%, transparent);
  pointer-events: none;
  z-index: 1;

  &._with-scroll-lock {
    pointer-events: unset;
  }
  > * {
    pointer-events: auto;
  }
`

const popupClass = css`
  position: relative;
  padding: 8px;
  background-color: ${style.themeColors.backgroundPopout};
  filter: drop-shadow(0px 4px 16px rgba(0, 0, 0, 0.25));
  isolation: isolate;

  
  > button {
    color: ${style.themeColors.textDefault};
  }

  `

export interface DropdownOption {
  id: string
  title: string
  iconUrl?: string
  className?: string
}

const baseDropDownClass = css`
  &._disabled {
    color: ${style.themeColors.textDisabled};
  }
`

export const Dropdown: FC<
  {
    ref?: React.Ref<HTMLDivElement>
    placement?: Placement | undefined
    className?: string
    popupClassName?: string
    overlayClassName?: string
    dropdownContent: ReactNode
    popupRef?: Ref<HTMLDivElement>
    open: boolean | undefined
    offset?: OffsetOptions
    disabled?: boolean
    onOpenChange: (open: boolean, event?: Event, reason?: OpenChangeReason) => unknown
    onClick?: DOMAttributes<HTMLDivElement>['onClick']
    dismissOnClickOutside?: boolean
    arrowOffset?: number | 'start' | 'end' | string
  } & (
    | {
        children: ReactNode
        overridingPosition?: undefined
      }
    | {
        children?: undefined
        overridingPosition: ReferenceType | null
      }
  )
> = ({
  ref,
  placement = 'bottom-start',
  className,
  popupClassName,
  overlayClassName,
  dropdownContent,
  open,
  disabled,
  offset: offsetDistance = 5,
  dismissOnClickOutside = true,
  popupRef,
  onOpenChange,
  onClick,
  arrowOffset,
  children,
  overridingPosition,
}) => {
  const arrowRef = useRef(null)
  const { refs, context, floatingStyles } = useFloating({
    placement,
    open,
    onOpenChange: onOpenChange,
    middleware: [
      offset(offsetDistance),
      flip(),
      shift(),
      arrow({
        element: arrowRef,
        padding: 8,
      }),
    ],
    whileElementsMounted: autoUpdate,
  })

  const dismiss = useDismiss(context, {
    enabled: dismissOnClickOutside,
  })
  const { getFloatingProps } = useInteractions([dismiss])
  const mergedRefs = useMergeRefs([ref, refs.setReference])

  useEffect(() => {
    if (overridingPosition) {
      refs.setPositionReference(overridingPosition)
    }
  }, [overridingPosition, refs.setPositionReference])

  return (
    <>
      {children && (
        <div
          className={cx(baseDropDownClass, className, '_dropdown-anchor', disabled && '_disabled')}
          ref={mergedRefs}
          onClick={disabled ? undefined : onClick}
          onKeyDown={
            disabled
              ? undefined
              : (evt) => {
                  if (evt.key === 'Enter' || evt.key === ' ') {
                    evt.preventDefault()
                    onOpenChange(!open, evt.nativeEvent, 'click')
                  }
                }
          }
          tabIndex={0}
          role="button"
        >
          {children}
        </div>
      )}

      {open && (
        <FloatingPortal>
          <FloatingOverlay
            unselectable={dismissOnClickOutside ? 'off' : 'on'}
            className={cx(overlayClass, dismissOnClickOutside && '_with-scroll-lock', overlayClassName)}
            lockScroll={dismissOnClickOutside}
            ref={popupRef}
          >
            <FloatingFocusManager
              context={context}
              disabled={!dismissOnClickOutside}
              modal={dismissOnClickOutside}
              closeOnFocusOut={dismissOnClickOutside}
            >
              <div
                className={cx(popupClass, popupClassName)}
                ref={refs.setFloating}
                style={floatingStyles}
                {...getFloatingProps()}
                onClick={(evt) => {
                  if (evt.isPropagationStopped()) {
                    evt.stopPropagation()
                  }
                }}
              >
                <FloatingArrow
                  className="_floating-arrow"
                  ref={arrowRef}
                  context={context}
                  width={18}
                  fill={style.themeColors.backgroundPopout}
                  staticOffset={arrowOffset}
                />
                {dropdownContent}
              </div>
            </FloatingFocusManager>
          </FloatingOverlay>
        </FloatingPortal>
      )}
    </>
  )
}
